import React from "react";
import { toAbsoluteUrl } from "../../../../_metronic/_helpers";
import { useTranslation } from "../../../i18n/LanguageContext";

// The showcase artwork is 680x944. Art and headline share that coordinate system inside
// one SVG, so the text scales and crops exactly like the image ("cover", anchored top).
const ART_WIDTH = 680;
const ART_HEIGHT = 944;

// Headline box in artwork units. Poppins 600 at 50/58 puts the first baseline 46.5 below
// the box top; the original lettering's first baseline sat at y=414 (sign-up) / 330 (login).
const HEADLINE_X = 40;
const HEADLINE_WIDTH = 600;
const BASELINE_OFFSET = 46.5;

// English headlines are at most 63 characters (3 lines). Longer translations (e.g. French)
// would wrap to 5 lines and run into the illustration, so they get a smaller size.
const LONG_HEADLINE = 70;

// Text-free artwork (headline stripped from the original exports) + its headline string.
export const SHOWCASES = {
   login: {
      art: "/media/bg/login-showcase-art.svg",
      headlineKey: "showcase-login-headline",
      firstBaseline: 330,
      // The dashboard screenshot starts right under line 3, so step down sooner.
      longAfter: 60
   },
   organization: {
      art: "/media/bg/signup-organization-showcase-art.svg",
      headlineKey: "showcase-organization-headline"
   },
   donor: {
      art: "/media/bg/signup-donor-showcase-art.svg",
      headlineKey: "showcase-donor-headline"
   },
   expert: {
      art: "/media/bg/signup-expert-showcase-art.svg",
      headlineKey: "showcase-expert-headline"
   }
};

/** Splits "plain [[highlighted]] plain" into spans; [[...]] renders in the accent colour. */
const renderHeadline = (text) =>
   text.split(/(\[\[.*?\]\])/).map((part, i) =>
      part.startsWith("[[") ? (
         <span key={i} className="kap-showcase__accent">
            {part.slice(2, -2)}
         </span>
      ) : (
         part
      )
   );

/**
 * The image card on the login and sign-up pages: text-free artwork plus a translated
 * headline drawn on top, so the headline follows the selected language.
 */
function ShowcaseCard({ name, className = "" }) {
   const { t } = useTranslation();
   const { art, headlineKey, firstBaseline = 414, longAfter = LONG_HEADLINE } = SHOWCASES[name];
   const headline = t(headlineKey);
   const plainHeadline = headline.replace(/\[\[|\]\]/g, "");

   return (
      <div className={`kap-login__showcase ${className}`}>
         <svg
            className="kap-showcase"
            viewBox={`0 0 ${ART_WIDTH} ${ART_HEIGHT}`}
            preserveAspectRatio="xMidYMin slice"
            role="img"
            aria-label={plainHeadline}
         >
            <image
               href={toAbsoluteUrl(art)}
               width={ART_WIDTH}
               height={ART_HEIGHT}
               preserveAspectRatio="xMidYMin slice"
            />
            <foreignObject
               x={HEADLINE_X}
               y={firstBaseline - BASELINE_OFFSET}
               width={HEADLINE_WIDTH}
               height={ART_HEIGHT - (firstBaseline - BASELINE_OFFSET)}
            >
               <p
                  className={`kap-showcase__headline${
                     plainHeadline.length > longAfter ? " kap-showcase__headline--long" : ""
                  }`}
                  aria-hidden="true"
               >
                  {renderHeadline(headline)}
               </p>
            </foreignObject>
         </svg>
      </div>
   );
}

export default ShowcaseCard;
